import React, { useState } from "react";
import { cn } from "@/lib/utils";
import { ArrowRight, ChevronDown } from "lucide-react";

export interface FunnelStage {
  name: string;
  count: number;
  amount?: number;
  color?: string;
}

interface FunnelConversionChartProps {
  stages: FunnelStage[];
  formatMoney?: (v: number) => string;
  onStageClick?: (stage: FunnelStage) => void;
  className?: string;
}

const FUNNEL_COLORS = [
  "#3b82f6", // blue-500
  "#06b6d4", // cyan-500
  "#8b5cf6", // violet-500
  "#f59e0b", // amber-500
  "#10b981", // emerald-500
];

export function FunnelConversionChart({
  stages,
  formatMoney = (v) => `₹${v.toLocaleString("en-IN")}`,
  onStageClick,
  className,
}: FunnelConversionChartProps) {
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);

  if (!stages || stages.length === 0) {
    return (
      <div className={cn("p-6 text-center text-xs text-muted-foreground", className)}>
        No pipeline stages defined
      </div>
    );
  }

  const initialCount = stages[0]?.count || 1;
  const maxCount = Math.max(1, ...stages.map((s) => s.count));

  return (
    <div className={cn("flex flex-col gap-2.5 w-full", className)}>
      {stages.map((stage, idx) => {
        const isHovered = hoveredIdx === idx;
        const color = stage.color || FUNNEL_COLORS[idx % FUNNEL_COLORS.length];
        const overallConv = ((stage.count / initialCount) * 100).toFixed(0);
        const prevCount = idx > 0 ? (stages[idx - 1]?.count ?? null) : null;
        const stepConv = prevCount && prevCount > 0 ? ((stage.count / prevCount) * 100).toFixed(0) : null;
        const widthPct = Math.max(18, (stage.count / maxCount) * 100);

        return (
          <div
            key={stage.name + idx}
            className="flex flex-col gap-1"
            onMouseEnter={() => setHoveredIdx(idx)}
            onMouseLeave={() => setHoveredIdx(null)}
          >
            {/* Step Row */}
            <div
              onClick={() => onStageClick?.(stage)}
              className={cn(
                "group relative flex items-center justify-between p-2.5 rounded-xl border transition-all cursor-pointer select-none",
                isHovered
                  ? "border-primary/50 bg-accent/40 shadow-xs"
                  : "border-border/60 bg-card/60 hover:bg-accent/20"
              )}
            >
              {/* Left Stage Info */}
              <div className="flex items-center gap-2.5 min-w-0 z-10">
                <span
                  className="size-2.5 rounded-full shrink-0 shadow-2xs"
                  style={{ backgroundColor: color }}
                />
                <span className="text-xs font-bold text-foreground truncate max-w-[140px]">
                  {stage.name}
                </span>
                <span className="text-[11px] font-semibold text-muted-foreground tabular-nums">
                  ({stage.count} deal{stage.count !== 1 ? "s" : ""})
                </span>
              </div>

              {/* Background Width Bar */}
              <div
                className="absolute inset-y-1.5 left-1.5 rounded-lg opacity-15 transition-all duration-300 pointer-events-none"
                style={{
                  width: `calc(${widthPct}% - 12px)`,
                  backgroundColor: color,
                }}
              />

              {/* Right Value & Conversion */}
              <div className="flex items-center gap-3 z-10 shrink-0 tabular-nums">
                {stage.amount !== undefined && (
                  <span className="text-xs font-bold text-foreground">
                    {formatMoney(stage.amount)}
                  </span>
                )}
                <span
                  className="px-2 py-0.5 rounded-md text-[10px] font-bold text-white shadow-2xs"
                  style={{ backgroundColor: color }}
                >
                  {overallConv}%
                </span>
              </div>
            </div>

            {/* Step drop-off indicator between rows */}
            {idx < stages.length - 1 && stepConv && (
              <div className="flex items-center gap-1.5 px-4 text-[10px] font-semibold text-muted-foreground/80">
                <ChevronDown className="size-3 text-muted-foreground/60 shrink-0" />
                <span>{stepConv}% conversion to {stages[idx + 1]?.name}</span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
